/**
 * Types for the realistic backtest engine
 */

export interface HistoricalPrice {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  symbol: string;
  isSynthetic?: boolean;  // Flag to indicate that this is synthetic data
  isFuture?: boolean;     // Flag to indicate that this is future data
}

export type TradeDirection = 'long' | 'short' | 'exit';

export interface BacktestPosition {
  symbol: string;
  quantity: number;
  entryPrice: number;
  entryDate: string;
  direction: 'long' | 'short';
  currentPrice?: number;
  stopLossPrice?: number;
  takeProfitPrice?: number;
}

export interface BacktestPortfolio {
  cash: number;
  positions: Record<string, BacktestPosition>;
  equity: Array<{date: string; value: number}>;
  trades: BacktestTrade[];
  initialCapital: number;
}

export interface BacktestTrade {
  symbol: string;
  entryDate: string;
  exitDate: string;
  entryPrice: number;
  exitPrice: number;
  quantity: number;
  profit: number;
  profitPercent: number;
  direction: 'long' | 'short';
  reason: string;
}

export interface BacktestSignal {
  symbol: string;
  direction: TradeDirection;
  strength: number; // Signal strength/conviction (0-1)
  price?: number;
  timestamp: string;
  modelType: string;
  reason?: string;
}

export interface SymbolSettings {
  symbol: string;
  profitTarget?: string; // Percentage as string (e.g., "10")
  stopLoss?: string; // Percentage as string (e.g., "2")
  maxPositionSize?: string; // Percentage as string
}

export interface BacktestOptions {
  startDate: string;
  endDate: string;
  timeframe: 'minute' | '5minute' | '15minute' | 'hour' | 'day' | 'week';
  initialCapital: number;
  symbols: string[];
  symbolSettings: SymbolSettings[];
  commissionRate: number;
  slippageRate: number;
  maxPositionSize: number; // As decimal (0.1 = 10%)
  maxStrategyExposure: number; // As decimal (1.0 = 100%)
  defaultProfitTarget: number; // As decimal (0.1 = 10%)
  defaultStopLoss: number; // As decimal (0.02 = 2%)
}

export interface BacktestPerformance {
  totalReturn: number;
  annualizedReturn: number;
  maxDrawdown: number;
  sharpeRatio: number;
  trades: number;
  winRate: number;
  profitFactor: number;
  averageProfitPerTrade: number;
  averageLossPerTrade: number;
  averageTradeDuration: number;
}

export interface BacktestResult {
  performance: BacktestPerformance;
  equityCurve: Array<{date: string; value: number}>;
  trades: BacktestTrade[];
  drawdownSeries: Array<{date: string; drawdown: number}>;
  monthlyReturns: Array<{month: string; return: number}>;
  message?: string;
}

export interface HistoricalDataProvider {
  fetchHistoricalData(
    symbols: string[], 
    startDate: string, 
    endDate: string, 
    timeframe: string
  ): Promise<HistoricalPrice[]>;
}

export enum TradeExitReason {
  TAKE_PROFIT = 'Take profit',
  STOP_LOSS = 'Stop loss',
  SIGNAL_EXIT = 'Signal exit',
  END_OF_BACKTEST = 'End of backtest',
  RISK_MANAGEMENT = 'Risk management',
  USER_DEFINED = 'User defined exit'
}